import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import PersonaBadge from '../common/PersonaBadge';
import UserBadge from '../common/UserBadge';
import { formatDistanceToNow } from 'date-fns';
import { FiMessageSquare, FiClock, FiTrash2, FiArchive, FiArrowRight, FiMapPin } from 'react-icons/fi';
import type { ChatSession } from '../../services/chatService';
import { sessionRoute } from '../../utils/sessionRoute';

interface ChatSessionCardProps {
  session: ChatSession;
  onDelete?: (sessionId: string) => void;
  onArchive?: (sessionId: string) => void;
  onClick?: (sessionId: string) => void;
}

const STATUS_PILL: Record<string, { bg: string; text: string; label: string; pulse?: boolean }> = {
  active: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Active', pulse: true },
  idle: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Idle' },
  archived: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Archived' },
};

function relTime(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 'Recently';
    return formatDistanceToNow(d, { addSuffix: true });
  } catch { return 'Recently'; }
}

/** Extract a topic line from the last message or persona specialization */
function extractTopic(session: ChatSession): string {
  if (session.last_message) {
    const msg = session.last_message.replace(/^(system|user|assistant):\s*/i, '');
    return msg.length > 200 ? msg.slice(0, 197) + '...' : msg;
  }
  return session.persona_specialization || 'Chat session';
}

/** WIP hazard-tape banner across the top of a Trailhead card (spec rev 2 §4.5). */
const WipTapeBanner: React.FC = () => (
  <div
    data-testid="wip-tape"
    className="rounded-t-lg -mx-5 -mt-5 mb-3 py-1 text-center"
    style={{
      backgroundImage:
        'repeating-linear-gradient(45deg, #f5c518 0 12px, #1a1a1a 12px 24px)',
    }}
  >
    <span className="bg-amber-300/90 text-gray-900 px-2 py-0.5 rounded text-[10px] font-bold tracking-widest">
      WIP · TRAILHEAD
    </span>
  </div>
);

export const ChatSessionCard: React.FC<ChatSessionCardProps> = ({
  session, onDelete, onArchive, onClick
}) => {
  const { darkMode } = useTheme();
  const [hovered, setHovered] = useState(false);
  const statusInfo = STATUS_PILL[session.status || 'active'] || STATUS_PILL.active;
  const topic = extractTopic(session);
  const isTrail = !!session.is_trailhead;
  const bg = darkMode ? 'bg-gray-800' : 'bg-white shadow-sm';
  const accent = isTrail
    ? (darkMode ? 'border-amber-600' : 'border-amber-500')
    : (darkMode ? 'border-emerald-600' : 'border-emerald-500');
  const hoverShadow = darkMode
    ? (isTrail ? 'hover:shadow-[0_0_15px_rgba(245,158,11,0.18)]' : 'hover:shadow-[0_0_15px_rgba(16,185,129,0.15)]')
    : 'hover:shadow-md';

  return (
    <div
      data-testid={`scard-${session.id}`}
      className={`rounded-lg shadow-lg border-l-4 p-5 cursor-pointer transition-all duration-200 hover:translate-y-[-1px] ${bg} ${accent} ${hoverShadow}`}
      onClick={() => onClick?.(session.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {isTrail && <WipTapeBanner />}
      {/* Header: PersonaBadge + name + status pill + time */}
      <div className="flex items-center gap-3 mb-3">
        <PersonaBadge
          personaId={session.persona_id}
          personaName={session.persona_name}
          size="md"
          clickable={false}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-semibold truncate ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              {session.persona_name.replace(/_/g, ' ')}
            </span>
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${statusInfo.bg} ${statusInfo.text}`}>
              {statusInfo.pulse && <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse mr-1" />}
              {statusInfo.label}
            </span>
          </div>
          {session.persona_specialization && (
            <p className={`text-xs truncate ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {session.persona_specialization}
            </p>
          )}
          {session.owner !== undefined && (
            <div className="mt-0.5">
              <UserBadge owner={session.owner} size="sm" />
            </div>
          )}
        </div>
        <span className={`text-xs shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {relTime(session.last_activity)}
        </span>
      </div>

      {/* Trail subtitle (mission + residents) on Trailhead cards */}
      {isTrail && (session.hill_to_climb || session.trail_name) && (
        <div className={`text-xs mb-2 truncate ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
          🥾 {session.hill_to_climb || session.trail_name}
          {session.resident_count ? ` · ${session.resident_count} residents` : ''}
        </div>
      )}

      {/* Topic / last message preview */}
      <div className={`text-sm mb-3 line-clamp-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {topic}
      </div>

      <style>{`.line-clamp-3{display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}`}</style>

      {/* Metrics row + actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`flex items-center gap-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            <FiMessageSquare className="w-3 h-3" /> {session.message_count}
          </span>
          <span className={`flex items-center gap-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            <FiClock className="w-3 h-3" /> {relTime(session.created_at)}
          </span>
          {(session.geojson_artifact_count ?? 0) > 0 && (
            <span className={`flex items-center gap-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <FiMapPin className="w-3 h-3" /> {session.geojson_artifact_count}{' '}
              {session.geojson_artifact_count === 1 ? 'map' : 'maps'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {hovered && onArchive && (
            <button onClick={(e) => { e.stopPropagation(); onArchive(session.id); }}
              className={`p-1 rounded ${darkMode ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-100 text-gray-400'}`}>
              <FiArchive className="w-3.5 h-3.5" />
            </button>
          )}
          {hovered && onDelete && (
            <button onClick={(e) => { e.stopPropagation(); onDelete(session.id); }}
              className={`p-1 rounded ${darkMode ? 'hover:bg-red-900/30 text-red-400' : 'hover:bg-red-50 text-red-500'}`}>
              <FiTrash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <Link
            to={sessionRoute(session)}
            onClick={(e) => e.stopPropagation()}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
              isTrail
                ? (darkMode ? 'text-amber-400 hover:bg-amber-900/20' : 'text-amber-600 hover:bg-amber-50')
                : (darkMode ? 'text-emerald-400 hover:bg-emerald-900/20' : 'text-emerald-600 hover:bg-emerald-50')
            }`}
          >
            {isTrail ? 'View Trail' : 'Continue'} <FiArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};
